import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api, { NOT_AUTHORIZED, ACCESS_CHANGED_EVENT } from '../services/api';

const AuthContext = createContext(null);

/** Same set as user_access.READ_ONLY_ROLES. */
const READ_ONLY_ROLES = new Set(['read_only', 'cpa', 'cpa_usa', 'cpa_india']);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Signed in with Google, but not an active FinOps user: distinct from
  // "not signed in", so the app can say so instead of offering sign-in again.
  const [accessDenied, setAccessDenied] = useState(false);

  // Only the newest answer counts: re-checks run on every navigation, and an
  // older reply arriving late must not overwrite a newer one.
  const latestCheck = useRef(0);

  const checkAuth = useCallback(async () => {
    const check = ++latestCheck.current;
    try {
      const res = await api.get('/api/auth/me');
      if (check !== latestCheck.current) return;
      setUser(res.data);
      setAccessDenied(false);
    } catch (e) {
      if (check !== latestCheck.current) return;
      const status = e.response?.status;
      // 401/403 are the backend's answer about this person. Anything else --
      // a dropped connection, a 5xx -- says nothing about them, so a re-check
      // keeps who we already know rather than signing them out. (On the first
      // check there is nobody yet, and the sign-in page follows as before.)
      if (status === 401 || status === 403) {
        setUser(null);
        setAccessDenied(status === 403 && e.response?.data?.detail === NOT_AUTHORIZED);
      }
    } finally {
      if (check === latestCheck.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // The backend re-reads permissions on every request, so a change an
  // administrator makes applies at once there. Re-reading /api/auth/me on
  // each navigation brings the menu and the no-access screen along with it,
  // rather than at the next sign-in.
  const { pathname } = useLocation();
  const firstPath = useRef(true);
  useEffect(() => {
    if (firstPath.current) { firstPath.current = false; return; }
    checkAuth();
  }, [pathname, checkAuth]);

  useEffect(() => {
    window.addEventListener(ACCESS_CHANGED_EVENT, checkAuth);
    return () => window.removeEventListener(ACCESS_CHANGED_EVENT, checkAuth);
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {}
    setUser(null);
    window.location.href = '/login';
  };

  const isAdmin = () => user?.role === 'admin';

  /** Signed in is not enough: the backend says whether FinOps grants this
   *  person anything at all. An active user with no permission is signed in
   *  and authorized for nothing, and sees only the no-access screen. */
  const authorized = !!user?.authorized;

  /** Can this user change anything? Mirrors the backend, which refuses every
   *  write from a read-only role in get_current_user. CPA roles are read-only;
   *  this used to count `cpa` as a writer. */
  const canWrite = () => !!user && !READ_ONLY_ROLES.has(user.role);

  /** User's configured timezone (IANA), defaulting to UTC. */
  const timezone = user?.timezone || 'UTC';

  /** Does this user hold a module permission?
   *
   *  This is a mirror of the backend's `permission_utils.has_permission`, and
   *  it has to stay one. It decides what the navigation offers; the backend
   *  decides what anyone can actually do. Hiding a link is a courtesy -- it
   *  keeps people out of screens that would only fail -- and is never the
   *  security boundary. Navigating straight to a URL reaches the same API
   *  calls, which are authorised server-side on every request.
   *
   *  It used to open by comparing `user.email` against one hard-coded address
   *  for `accounts_usa`, before the admin check and with no backend counterpart.
   *  That hid every Eyvo USA screen -- transactions, expenses, pending review,
   *  bank connections, imported transactions, auto-categorise -- from every
   *  other account, while the API happily served the same data to any admin.
   *  It read as a security control and was not one.
   */
  const hasPermission = (perm) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    const perms = Array.isArray(user.permissions) ? user.permissions : [];
    if (perms.includes(perm)) return true;
    // Legacy 'accounts' satisfies both entity-specific variants. Previously
    // this covered only India, which would have kept the USA nav hidden even
    // with the email gate gone.
    if ((perm === 'accounts_usa' || perm === 'accounts_india')
        && perms.includes('accounts')) return true;
    // Either entity-specific permission satisfies legacy 'accounts'
    if (perm === 'accounts' && (perms.includes('accounts_usa') || perms.includes('accounts_india'))) return true;
    // CPA roles carry implicit entity access. Same sets as _CPA_ENTITY_MAP.
    const cpaMap = {
      cpa:        new Set(['accounts_usa', 'accounts_india', 'accounts']),
      cpa_usa:    new Set(['accounts_usa', 'accounts']),
      cpa_india:  new Set(['accounts_india', 'accounts']),
    };
    if (cpaMap[user.role]?.has(perm)) return true;
    return false;
  };

  /** Where "/" leads: the first screen this user holds. Never the dashboard
   *  by default -- it is a permission like the others, and a user without it
   *  would only be turned away. */
  const landingPath = () => {
    if (hasPermission('dashboard')) return '/dashboard';
    if (hasPermission('accounts_usa')) return '/accounts/usa/overview';
    if (hasPermission('accounts_india')) return '/accounts/india/overview';
    if (hasPermission('invoices')) return '/invoices';
    if (hasPermission('clients')) return '/clients';
    if (hasPermission('suppliers')) return '/suppliers';
    return '/welcome';
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, accessDenied, authorized, checkAuth, logout, isAdmin, hasPermission, landingPath, canWrite, timezone }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
